import { FormEvent, useState } from 'react';
import { useTranslations } from 'next-intl';

import { urls } from '@zapier/identity-utils';
import { fetch } from '../../../utils';

import { parseHash } from './parseHash';

type Input = {
  hash: string;
};

type ResetPasswordStatus = 'initial' | 'submitting' | 'success';

type ResetPasswordData = {
  password1: string;
  password2: string;
};

type ResetPasswordResult = {
  errors?: string[];
};

type ResetPasswordErrorCode = 'invalid' | 'too-frequent' | 'unknown';

type ResetPasswordError =
  | { kind: 'code'; code: ResetPasswordErrorCode }
  | { kind: 'server'; message: string };

async function submitResetPassword(
  data: ResetPasswordData,
  hash: string
): Promise<ResetPasswordError | ''> {
  const { code, username } = parseHash(hash);

  const resp = await fetch<ResetPasswordResult>(urls.RESET_PASSWORD_API_URL, {
    method: 'POST',
    body: {
      ...data,
      code,
      username,
    },
  });

  if (resp.body.errors) {
    return { kind: 'server', message: resp.body.errors[0] };
  }
  if (resp.status === 200) return '';
  if (resp.status === 400 || resp.status == 401 || resp.status === 403) {
    return { kind: 'code', code: 'invalid' };
  }
  if (resp.status == 429) return { kind: 'code', code: 'too-frequent' };
  return { kind: 'code', code: 'unknown' };
}

export function useResetPassword({ hash }: Input) {
  const t = useTranslations('reset-password');
  const [formData, setFormData] = useState<ResetPasswordData>({
    password1: '',
    password2: '',
  });
  const [error, setError] = useState<string | undefined>();
  const [status, setStatus] = useState<ResetPasswordStatus>('initial');

  function resultToMessage(result: ResetPasswordError | ''): string {
    if (result === '') return '';
    if (result.kind === 'server') return result.message;
    switch (result.code) {
      case 'invalid':
        return t('errors.invalid');
      case 'too-frequent':
        return t('errors.too-frequent');
      case 'unknown':
        return t('errors.unknown');
    }
  }

  async function handleSubmit(e?: FormEvent<HTMLFormElement>) {
    if (e) {
      e.preventDefault();
    }

    setStatus('submitting');
    setError(undefined);

    const error = resultToMessage(await submitResetPassword(formData, hash));

    setError(error);
    setStatus(error ? 'initial' : 'success');
  }

  function handleChange(field: string, value: string) {
    setFormData(data => ({
      ...data,
      [field]: value,
    }));
  }

  return {
    canSubmit:
      formData.password1.length > 0 &&
      formData.password2.length > 0 &&
      status !== 'submitting',
    error,
    formData,
    handleChange,
    handleSubmit,
    status,
  };
}
